/**
 * The new medicine watch card on the resident chart (V8 "New medicine watch", V9 L2406-2420,
 * the design's mwChart over med-watch.js). Every value comes from GET /residents/:id/med-watches
 * (src/aithora/v3/worklist/med-watch-routes.ts), whose text is already in the viewer's language.
 *
 *   - A nurse, the Facility Admin and Aithora's team (read only) get the steps: the next step
 *     first with why it fired, the interaction line, Show all steps, and "Result reviewed, close
 *     watch" once every step is done. Care staff get the signs only (the server decides).
 *   - The "Tell the nurse if" signs are tap-to-report for the building's staff; each opens a
 *     Guardian case on the care team's bell. Read-only viewers see them as plain words.
 *   - Dose-reduction plans, night checks and the family note have no back end yet: off.
 */
import type { MedWatch, MedWatchSignsOnly, MedWatchStep, ResidentMedWatchesResponse } from '@aithora/shared';
import { pre } from './dom';

type Ev = { preventDefault?: () => void } | undefined;

export interface MedWatchUi {
  /** Watches whose other steps are shown. */
  open: Record<string, boolean>;
}

export interface MedWatchActs {
  setOpen: (watchId: string, open: boolean) => void;
  done: (watch: MedWatch, step: MedWatchStep) => void;
  close: (watch: MedWatch) => void;
  report: (watchId: string, signKey: string) => void;
}

export interface MedWatchInput {
  data: ResidentMedWatchesResponse | null;
  /** May act on resident care (the building's own staff, not viewing as anyone). */
  canAct: boolean;
  /** May report a sign (care staff and the Facility Admin, POST /med-watches/:id/signs). */
  canReport: boolean;
  nowMs: number;
  ui: MedWatchUi;
  act: MedWatchActs;
}

const DAY = 86_400_000;
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Today", "Tonight", "Tomorrow", a weekday this week, or "Day 30" of the watch (design due()). */
export function dueLabel(step: Pick<MedWatchStep, 'dueOn' | 'dueAt' | 'dayOffset'>, nowMs: number): string {
  const now = new Date(nowMs);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const [y, m, d] = step.dueOn.split('-').map(Number);
  const days = Math.round((Date.UTC(y, (m || 1) - 1, d || 1) - today) / DAY);
  if (days <= 0) {
    const at = Date.parse(step.dueAt);
    return Number.isFinite(at) && new Date(at).getHours() >= 18 ? 'Tonight' : 'Today';
  }
  if (days === 1) return 'Tomorrow';
  if (days < 7) return WEEKDAY[new Date(Date.UTC(y, (m || 1) - 1, d || 1)).getUTCDay()];
  return 'Day ' + (step.dayOffset + 1);
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/** One card's values, from a watch with its steps (or the signs only, for care staff). */
function card(w: MedWatch | MedWatchSignsOnly, steps: boolean, inp: MedWatchInput) {
  const full = steps ? (w as MedWatch) : null;
  const open = full ? full.steps.filter((s) => s.status === 'open' || s.status === 'snoozed') : [];
  const next = full?.next ?? open[0] ?? null;
  const rest = open.filter((s) => s.id !== next?.id);
  const shown = !!inp.ui.open[w.id];
  const reported = new Map((full?.reportedSigns ?? []).map((r) => [r.key, r]));
  const meta = w.className + ' · Day ' + w.day + ' of ' + w.days + (full?.startedByName ? ' · started by ' + full.startedByName : '');
  return {
    mwShow: true,
    mwNurse: steps,
    mwDrug: w.medicine ?? w.className,
    mwMeta: meta,
    mwHasNext: !!next,
    mwCanAct: inp.canAct && steps,
    mwNextK: next?.label ?? '',
    mwNextDue: next ? dueLabel(next, inp.nowMs) + (next.late ? ' · late' : '') : '',
    mwNextWhy: next?.why ?? '',
    mwNextDone: (e: Ev) => { pre(e); if (full && next && inp.canAct) inp.act.done(full, next); },
    mwInteract: full ? full.interactions.join(' ') : '',
    mwHasRest: rest.length > 0,
    mwOpen: shown && rest.length > 0,
    mwRest: rest.map((s) => ({ k: s.label, due: dueLabel(s, inp.nowMs) + (s.late ? ' · late' : ''), why: s.why })),
    mwToggle: (e: Ev) => { pre(e); inp.act.setOpen(w.id, !shown); },
    mwToggleLabel: shown ? 'Hide other steps' : 'Show all ' + (rest.length + 1) + ' steps',
    mwAllDone: !!full && full.canClose && !next && inp.canAct,
    mwClose: (e: Ev) => { pre(e); if (full && inp.canAct) inp.act.close(full); },
    mwSignHead: inp.canReport ? 'Seen one of these? Tap it to report.' : 'Tell the nurse if',
    mwSignRows: w.signs.map((g) => {
      const r = reported.get(g.key);
      return {
        k: g.text,
        reported: !!r,
        reportedAt: r ? clock(r.at) : '',
        open: !r && inp.canReport,
        plain: !r && !inp.canReport,
        report: (e: Ev) => { pre(e); if (inp.canReport) inp.act.report(w.id, g.key); },
      };
    }),
    mwSignNote: inp.canReport
      ? 'A sign you report reaches the nurse and the care team on Guardian at once.'
      : 'Staff on the floor report these; each one reaches the nurse and the care team on Guardian.',
    mwGdrShow: false,
    mwNightShow: false,
    mwFamShow: false,
    mwSrc: 'Aithora reminds; the nurse and prescriber decide.',
  };
}

export function medWatchVals(inp: MedWatchInput) {
  const d = inp.data;
  if (!d) return { mwCards: [] };
  const steps = d.view === 'steps';
  return { mwCards: (d.watches as Array<MedWatch | MedWatchSignsOnly>).map((w) => card(w, steps, inp)) };
}
